import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Handshake, Loader2, Link2 } from 'lucide-react';
import { useVisibilitySettings, useUpdateVisibility } from '@/hooks/useConnectApi';

/**
 * Compact toggle shown at the top of Passport Mode for opting into OptiBay Connect.
 * Requires a ZIP code when enabling visibility to shops.
 * Shows a "Find a Shop" link when opted in.
 */
export function PassportConnectToggle() {
  const navigate = useNavigate();
  const { data: settings, isLoading } = useVisibilitySettings();
  const updateMutation = useUpdateVisibility();
  const [showZipInput, setShowZipInput] = useState(false);
  const [zip, setZip] = useState('');

  const isVisible = settings?.visible_to_shops ?? false;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setShowZipInput(true);
    } else {
      updateMutation.mutate({ visible_to_shops: false });
    }
  };

  const handleConfirmZip = () => {
    const trimmed = zip.trim();
    if (!/^\d{5}$/.test(trimmed)) return;
    updateMutation.mutate(
      { visible_to_shops: true, location_zip: trimmed },
      { onSuccess: () => setShowZipInput(false) },
    );
  };

  if (isLoading) {
    return (
      <Card className="border-primary/20">
        <CardContent className="py-3 px-4 flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading connect settings...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardContent className="py-3 px-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Handshake className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">OptiBay Connect</p>
              <p className="text-xs text-muted-foreground">
                {isVisible
                  ? 'Your passport is visible to recruiting shops'
                  : 'Make your passport discoverable to shops'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isVisible && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => navigate('/connect')}
              >
                <Link2 className="h-3.5 w-3.5 mr-1" />
                Find a Shop
              </Button>
            )}
            <Switch
              checked={isVisible}
              onCheckedChange={handleToggle}
              disabled={updateMutation.isPending || showZipInput}
            />
          </div>
        </div>

        {showZipInput && (
          <div className="flex items-center gap-2 pt-1">
            <Input
              placeholder="Enter ZIP code"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
              className="h-8 w-32 text-sm"
              maxLength={5}
            />
            <Button
              size="sm"
              className="h-8"
              onClick={handleConfirmZip}
              disabled={!/^\d{5}$/.test(zip.trim()) || updateMutation.isPending}
            >
              {updateMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Enable'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8"
              onClick={() => setShowZipInput(false)}
            >
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
